import React, { useState, useEffect, useCallback, useRef } from "react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Card } from "./ui";
import { formatMoney } from "../lib/utils";
import { exportRealizedCsv, exportRealizedPdf } from "../lib/realizedExport";
import { TrendingUp, Coins, Home, Car, Gem, Box, LineChart, Download, FileText, FileSpreadsheet } from "lucide-react";

const POS = "#10B981";
const NEG = "#EF4444";

const ASSET_ICON = { property: Home, vehicle: Car, collectible: Gem, precious_metal: Coins, other: Box };

function eventIcon(ev) {
  if (ev.source === "security") return TrendingUp;
  return ASSET_ICON[ev.asset_type] || Box;
}

export default function RealizedPnL({ entity, base, hidden, refreshKey }) {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [year, setYear] = useState("all");
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolio/realized?entity_id=${entity.id}`);
      setData(data);
    } catch { setData(null); }
  }, [entity.id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const events = data?.events || [];
  if (!data || events.length === 0) return null;

  // fiscal-year filter (FR fiscal year = calendar year)
  const allYears = [...new Set(events.map((e) => (e.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const activeYear = year !== "all" && !allYears.includes(year) ? "all" : year;
  const fe = activeYear === "all" ? events : events.filter((e) => (e.date || "").slice(0, 4) === activeYear);
  const r2 = (x) => Math.round(x * 100) / 100;
  const sumGain = (pred) => r2(fe.filter((e) => e.gain_base != null && pred(e)).reduce((s, e) => s + e.gain_base, 0));
  const secReal = sumGain((e) => e.source === "security");
  const astReal = sumGain((e) => e.source === "asset");
  const totReal = sumGain(() => true);
  const periodLabel = activeYear === "all" ? t("realized_all_years") : activeYear;
  const viewData = {
    ...data, events: fe, count: fe.length,
    securities_realized: secReal, assets_realized: astReal, total_realized: totReal,
  };

  // realized gain per calendar year (ascending) for the mini bar chart
  const yearTotals = {};
  events.forEach((e) => {
    if (e.gain_base == null) return;
    const y = (e.date || "").slice(0, 4);
    if (y) yearTotals[y] = (yearTotals[y] || 0) + e.gain_base;
  });
  const chartData = Object.keys(yearTotals).sort().map((y) => ({ year: y, gain: r2(yearTotals[y]) }));
  const showChart = chartData.length >= 2;

  const doExport = (kind) => {
    setMenuOpen(false);
    if (kind === "csv") exportRealizedCsv(viewData, entity, t, lang, periodLabel);
    else exportRealizedPdf(viewData, entity, t, lang, periodLabel);
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short", year: "numeric" });

  // signed money at cent precision, with a neutral zero (avoids the misleading "−0 €")
  const signed = (v, cur = base) => {
    const r = Math.round((Number(v) || 0) * 100) / 100;
    const color = r > 0 ? POS : r < 0 ? NEG : "var(--text-secondary)";
    const sign = r > 0 ? "+" : r < 0 ? "−" : "";
    const text = hidden ? "••••" : `${sign}${formatMoney(Math.abs(r), cur, { decimals: 2 })}`;
    return { color, text };
  };

  const detail = (ev) => {
    if (ev.source === "security") return `${t("realized_sold")} ${ev.quantity} × ${formatMoney(ev.price, ev.currency, { decimals: 2 })}`;
    if (ev.asset_type === "precious_metal") return `${ev.quantity} oz × ${formatMoney(ev.price, ev.currency, { decimals: 2 })}/oz`;
    return ev.mortgage_payoff ? `${t("assets_mortgage_payoff")} −${formatMoney(ev.mortgage_payoff, ev.currency, { decimals: 0 })}` : t("realized_asset_sale");
  };

  return (
    <Card className="mt-5" data-testid="realized-pnl-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <LineChart className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-semibold">{t("realized_title")}</h3>
          <span className="text-xs text-text-secondary">{viewData.count}</span>
        </div>
        <div className="flex items-center gap-4">
          {allYears.length > 0 && (
            <select
              value={activeYear}
              onChange={(e) => setYear(e.target.value)}
              data-testid="realized-year-select"
              aria-label={t("realized_period")}
              className="text-xs px-2 py-1.5 rounded-lg border bg-surface hover:bg-surface-hover transition-colors cursor-pointer"
            >
              <option value="all">{t("realized_all_years")}</option>
              {allYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
          <span className="text-xs text-text-secondary">{t("realized_securities")} <span style={{ color: signed(viewData.securities_realized).color }}>{signed(viewData.securities_realized).text}</span></span>
          <span className="text-xs text-text-secondary">{t("realized_assets")} <span style={{ color: signed(viewData.assets_realized).color }}>{signed(viewData.assets_realized).text}</span></span>
          <span className="text-sm font-semibold tabular-nums" data-testid="realized-total" style={{ color: signed(viewData.total_realized).color }}>
            {t("realized_total")} {signed(viewData.total_realized).text}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="realized-export-button"
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border hover:bg-surface-hover transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {t("realized_export")}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-44 rounded-lg border bg-surface shadow-lg z-20 py-1" data-testid="realized-export-menu">
                <button
                  type="button"
                  onClick={() => doExport("csv")}
                  data-testid="realized-export-csv"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-surface-hover transition-colors"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-text-secondary" />
                  {t("realized_export_csv")}
                </button>
                <button
                  type="button"
                  onClick={() => doExport("pdf")}
                  data-testid="realized-export-pdf"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-surface-hover transition-colors"
                >
                  <FileText className="w-3.5 h-3.5 text-text-secondary" />
                  {t("realized_export_pdf")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showChart && (
        <div className="mb-4" data-testid="realized-year-chart" style={{ width: "100%", height: 110 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 14, right: 4, left: 4, bottom: 0 }}>
              <XAxis dataKey="year" tickLine={false} axisLine={false}
                tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
              <Tooltip
                cursor={{ fill: "var(--surface-hover)" }}
                contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
                labelStyle={{ color: "var(--text-secondary)" }}
                formatter={(v) => [signed(v).text, t("realized_title")]} />
              <Bar dataKey="gain" radius={[4, 4, 0, 0]} isAnimationActive={false} cursor="pointer"
                onClick={(d) => { const yr = d?.year ?? d?.payload?.year; if (yr) setYear((y) => (y === yr ? "all" : yr)); }}>
                {chartData.map((d) => {
                  const dim = activeYear !== "all" && activeYear !== d.year;
                  return <Cell key={d.year} fill={d.gain >= 0 ? POS : NEG} fillOpacity={dim ? 0.3 : 1} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="space-y-1.5">
        {fe.length === 0 ? (
          <div className="text-sm text-text-secondary py-4 text-center" data-testid="realized-empty-period">{t("realized_no_events_period")}</div>
        ) : fe.map((ev, i) => {
          const Ico = eventIcon(ev);
          const g = ev.gain_base;
          return (
            <div key={i} className="rounded-lg border px-3 py-2 flex items-center gap-3 text-sm" data-testid={`realized-event-${i}`}>
              <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center shrink-0">
                <Ico className="w-3.5 h-3.5 text-text-secondary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{ev.name}
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full border text-text-secondary">{ev.source === "security" ? t("realized_security") : t("realized_asset")}</span>
                </div>
                <div className="text-[11px] text-text-secondary truncate">{fmtDate(ev.date)} · {detail(ev)}</div>
              </div>
              <div className="text-right shrink-0">
                {g != null ? (
                  <div className="text-sm font-semibold tabular-nums" style={{ color: signed(g).color }} data-testid={`realized-gain-${i}`}>
                    {signed(g).text}
                  </div>
                ) : <span className="text-[11px] text-text-secondary">—</span>}
                {ev.currency && ev.currency !== base && g != null && (
                  <div className="text-[10px] text-text-secondary">{signed(ev.gain, ev.currency).text}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
